import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ButtonGroup } from "primereact/buttongroup";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { SpeedDial } from "primereact/speeddial";
import {
  apiService,
  ScheduleResponse,
  SchedulePerson,
  ScheduleActivity,
  ScheduleRemark,
  SchedulePatch,
} from "../services/apiService";
import { useApiAuth } from "../hooks/useApiAuth";
import { useScheduleRealtime } from "../hooks/useScheduleRealtime";
import { ActivityBadge, ReferenceBadge, HourType } from "../components/Badges";
import { SchedulingModal } from "../components/SchedulingModal";
import { WeekRangeModal } from "../components/WeekRangeModal";
import { RangeDeleteModal } from "../components/RangeDeleteModal";
import { FilterManager } from "../components/FilterManager";
import { LiveStatusIndicator } from "../components/LiveStatusIndicator";
import { useSearch } from "../contexts/SearchContext";
import {
  getCurrentWeek,
  generateWeekRange,
  formatWeekTooltip,
  isWeekInPast,
  WeekInfo,
  weekToDate,
  dateToWeek,
} from "../utils/weekUtils";
import { SavedFilter, applyEmployeeFilter } from "../utils/filterUtils";
import { getRemarksForView } from "../utils/scheduleRemarkUtils";
import { ScheduleRemarkChips } from "../components/ScheduleRemarkChips";
import { applySchedulePatch, SchedulePatchChange } from "../utils/schedulePatch";
import { flashVisibleScheduleCells, SCHEDULE_CELL_FLASH_MS } from "../utils/scheduleCellFlash";

interface Project {
  id: string;
  activityId: number; // Numeric activity ID for API calls
  name: string;
  activity: string;
  client: string;
  project: string;
  hourType: HourType;
  reference: string;
  weeklyHours: { [weekKey: string]: number };
  comments: { [weekKey: string]: string };
  isReservation: boolean; // Flag indicating if this is a reservation
}

interface Employee {
  id: number;
  name: string;
  availability: { [weekKey: string]: number };
  projects: Project[];
  scheduleRemarks: ScheduleRemark[];
}

interface ProjectEmployee {
  id: number;
  name: string;
  weeklyHours: { [weekKey: string]: number };
  comments: { [weekKey: string]: string };
  isReservation: boolean;
  scheduleRemarks: ScheduleRemark[];
}

interface ProjectSummary {
  id: string;
  activityId: number; // Numeric activity ID for API calls
  name: string;
  activity: string;
  client: string;
  project: string;
  hourType: HourType;
  reference: string;
  isReservation: boolean;
  totalWeeklyHours: { [weekKey: string]: number };
  employees: ProjectEmployee[];
}

type ViewMode = "employee" | "project";

const getDataTableWrapper = (root: HTMLDivElement | null): HTMLElement | null =>
  (root?.querySelector(".p-datatable-wrapper") as HTMLElement | null) ?? null;

const captureDataTableScroll = (
  root: HTMLDivElement | null,
  scrollTopRef: { current: number },
  scrollLeftRef: { current: number },
  needRestoreRef: { current: boolean },
) => {
  try {
    const el = getDataTableWrapper(root);
    scrollTopRef.current = el?.scrollTop ?? 0;
    scrollLeftRef.current = el?.scrollLeft ?? 0;
    needRestoreRef.current = true;
  } catch {}
};

const restoreDataTableScroll = (
  root: HTMLDivElement | null,
  scrollTopRef: { current: number },
  scrollLeftRef: { current: number },
) => {
  try {
    const wrapper = getDataTableWrapper(root);
    if (wrapper) {
      wrapper.scrollTop = scrollTopRef.current || 0;
      wrapper.scrollLeft = scrollLeftRef.current || 0;
    }
  } catch {}
};

interface ScheduleViewSettings {
  currentWeek: number;
  currentYear: number;
  numberOfWeeks: number;
}

const getInitialScheduleViewSettings = (): ScheduleViewSettings => {
  const defaultSettings: ScheduleViewSettings = {
    currentWeek: getCurrentWeek(),
    currentYear: new Date().getFullYear(),
    numberOfWeeks: 12,
  };

  try {
    const savedWeek = localStorage.getItem("scheduleView.currentWeek");
    const savedYear = localStorage.getItem("scheduleView.currentYear");
    const savedNumberOfWeeks = localStorage.getItem(
      "scheduleView.numberOfWeeks",
    );

    if (!savedWeek || !savedYear || !savedNumberOfWeeks) {
      return defaultSettings;
    }

    const parsedWeek = parseInt(savedWeek, 10);
    const parsedYear = parseInt(savedYear, 10);
    const parsedNumberOfWeeks = parseInt(savedNumberOfWeeks, 10);

    if (
      Number.isNaN(parsedWeek) ||
      Number.isNaN(parsedYear) ||
      Number.isNaN(parsedNumberOfWeeks)
    ) {
      return defaultSettings;
    }

    return {
      currentWeek: parsedWeek,
      currentYear: parsedYear,
      numberOfWeeks: parsedNumberOfWeeks,
    };
  } catch (error) {
    console.warn("Failed to read view settings from localStorage:", error);
    return defaultSettings;
  }
};

// Transform API response to our internal format
const transformApiResponse = (apiData: ScheduleResponse): Employee[] => {
  return apiData.map((person: SchedulePerson) => {
    // Convert availability hours from week keys to week numbers
    const availability: { [weekKey: string]: number } = {};
    Object.entries(person.hours).forEach(([weekKey, hours]) => {
      availability[weekKey] = hours;
    });

    // Convert projects
    const projects: Project[] = person.children.map(
      (activity: ScheduleActivity) => {
        const weeklyHours: { [weekKey: string]: number } = {};
        const comments: { [weekKey: string]: string } = {};

        // Convert hours from week keys to week numbers
        Object.entries(activity.hours).forEach(([weekKey, hours]) => {
          weeklyHours[weekKey] = hours;
        });

        // Convert comments from week keys to week numbers
        Object.entries(activity.comments).forEach(([weekKey, comment]) => {
          comments[weekKey] = comment;
        });

        return {
          id: activity.activity_id.toString(),
          activityId: activity.activity_id,
          name: activity.name,
          activity: activity.activity,
          client: activity.client,
          project: activity.project,
          hourType: activity.hour_type as HourType,
          reference: activity.reference,
          weeklyHours,
          comments,
          isReservation: activity.reservation,
        };
      },
    );

    return {
      id: person.id,
      name: person.name,
      availability,
      projects,
      scheduleRemarks: person.schedule_remarks ?? [],
    };
  });
};

// Transform employee data to project-centric view
const transformToProjectView = (employees: Employee[]): ProjectSummary[] => {
  const projectMap = new Map<string, ProjectSummary>();

  employees.forEach((employee) => {
    employee.projects.forEach((project) => {
      const projectKey = `${project.client}-${project.project}-${project.activity}`;

      if (!projectMap.has(projectKey)) {
        projectMap.set(projectKey, {
          id: project.id,
          activityId: project.activityId,
          name: project.name,
          activity: project.activity,
          client: project.client,
          project: project.project,
          hourType: project.hourType,
          reference: project.reference,
          isReservation: project.isReservation,
          totalWeeklyHours: {},
          employees: [],
        });
      }

      const projectSummary = projectMap.get(projectKey)!;

      // Add employee to this project
      projectSummary.employees.push({
        id: employee.id,
        name: employee.name,
        weeklyHours: project.weeklyHours,
        comments: project.comments,
        isReservation: project.isReservation,
        scheduleRemarks: employee.scheduleRemarks,
      });

      // Add hours to project totals
      Object.entries(project.weeklyHours).forEach(([weekKey, hours]) => {
        projectSummary.totalWeeklyHours[weekKey] =
          (projectSummary.totalWeeklyHours[weekKey] || 0) + hours;
      });
    });
  });

  return Array.from(projectMap.values());
};

export const SchedulePage: React.FC = () => {
  const [initialViewSettings] = useState<ScheduleViewSettings>(() =>
    getInitialScheduleViewSettings(),
  );

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [expandedRows, setExpandedRows] = useState<any>({});
  const [weeks, setWeeks] = useState<WeekInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [renderKey, setRenderKey] = useState(0); // Force re-render key
  const [currentYear, setCurrentYear] = useState(
    initialViewSettings.currentYear,
  );
  const [currentWeek, setCurrentWeek] = useState(
    initialViewSettings.currentWeek,
  );
  const [numberOfWeeks, setNumberOfWeeks] = useState(
    initialViewSettings.numberOfWeeks,
  );
  const [viewStateReady, setViewStateReady] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("employee");
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<1 | -1>(1); // 1 for ascending, -1 for descending

  // Filter functionality
  const [activeFilter, setActiveFilter] = useState<SavedFilter | null>(null);

  // Search functionality
  const { searchTerm } = useSearch();

  // Drag and drop state
  const [dragData, setDragData] = useState<{
    projectId: string;
    employeeId: number;
    sourceWeekKey: string;
    hours: number;
    comment?: string;
  } | null>(null);
  // Hover highlighting is handled via DOM classes to avoid re-renders

  const handleDragEnd = useCallback(() => {
    setDragData(null);
    // DOM-based cleanup only, no state
    // Cleanup any DOM drop-zone markers without forcing React re-render
    try {
      document
        .querySelectorAll(".is-drop-zone, .is-drag-over")
        .forEach((el) => el.classList.remove("is-drop-zone", "is-drag-over"));
      document.body.classList.remove("dnd-active");
    } catch {}
  }, []);

  // ESC-to-cancel removed per request; global cleanup handled by drop/dragend

  // Modal state
  const [schedulingModalVisible, setSchedulingModalVisible] = useState(false);
  const [modalInitialData, setModalInitialData] = useState<any>(null);

  // SpeedDial modal states
  const [weekRangeModalVisible, setWeekRangeModalVisible] = useState(false);
  const [rangeDeleteModalVisible, setRangeDeleteModalVisible] = useState(false);
  const [rangeDeleteInitialData, setRangeDeleteInitialData] =
    useState<any>(null);

  // Use the custom authentication hook
  const {
    isApiReady,
    isLoading: authLoading,
    error: authError,
    hasAttemptedAuth,
    retryAuth,
    beginInteractiveLogin,
  } = useApiAuth();

  // Effect to initialize tooltips once after first successful load
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (employees.length > 0 && weeks.length > 0 && !loading) {
      if (!hasInitialized.current) {
        const timer = setTimeout(() => {
          setRenderKey((prev) => prev + 1);
        }, 150);
        hasInitialized.current = true;
        return () => clearTimeout(timer);
      }
    }
  }, [employees.length, weeks.length, loading]);

  // Track tooltip refresh separately from main render key to avoid viewport jumping
  const [tooltipRefreshKey, setTooltipRefreshKey] = useState(0);
  // Track data version and scroll state to preserve position across updates
  const [dataVersion, setDataVersion] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const prevScrollTopRef = useRef<number>(0);
  const prevScrollLeftRef = useRef<number>(0);
  const needRestoreScrollRef = useRef<boolean>(false);
  const latestLoadRequestRef = useRef(0);
  const recentMutationIdsRef = useRef<string[]>([]);
  const lastRevisionRef = useRef(0);
  const hasSnapshotRef = useRef(false);
  const catchingUpRef = useRef(false);
  const catchUpScheduleRef = useRef<
    (opts?: { allowSnapshotFallback?: boolean }) => Promise<void>
  >(async () => {});
  const pendingRemoteFlashChangesRef = useRef<SchedulePatchChange[] | null>(
    null,
  );

  const trackMutationId = useCallback((id: string) => {
    recentMutationIdsRef.current = [
      ...recentMutationIdsRef.current.slice(-49),
      id,
    ];
  }, []);

  // Effect to refresh tooltips when rows are expanded/collapsed (without affecting DataTable)
  useEffect(() => {
    // Only refresh if there are expanded rows that might contain new comment elements
    if (Object.values(expandedRows).some((expanded) => expanded === true)) {
      const timer = setTimeout(() => {
        // Update only tooltip refresh key, not the main render key
        setTooltipRefreshKey((prev) => prev + 1);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [expandedRows]);

  // Reinitialize comment tooltips after data refreshes
  useEffect(() => {
    // Debounced to allow DOM to settle
    const t = setTimeout(() => setTooltipRefreshKey((k) => k + 1), 0);
    return () => clearTimeout(t);
  }, [dataVersion]);

  // Refresh tooltips when search term changes (filtering affects visible rows)
  useEffect(() => {
    const timer = setTimeout(() => {
      setTooltipRefreshKey((prev) => prev + 1);
    }, 100);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Track retry attempts to trigger useEffect
  const [retryTrigger, setRetryTrigger] = useState(0);

  // Mark view-state hydration complete before first API fetch
  useEffect(() => {
    setViewStateReady(true);
  }, []); // Only run on mount

  // Save view settings to localStorage whenever they change
  useEffect(() => {
    if (!viewStateReady) {
      return;
    }

    try {
      localStorage.setItem("scheduleView.currentWeek", currentWeek.toString());
      localStorage.setItem("scheduleView.currentYear", currentYear.toString());
      localStorage.setItem(
        "scheduleView.numberOfWeeks",
        numberOfWeeks.toString(),
      );
    } catch (error) {
      console.warn("Failed to save view settings to localStorage:", error);
    }
  }, [currentWeek, currentYear, numberOfWeeks, viewStateReady]);

  // Retry a failed load. Always try to recover the session first: silent
  // refresh can return a cached token the API already rejected, so Retry
  // must force-refresh and fall back to interactive login.
  const retryLoadData = useCallback(async () => {
    setApiError(null);
    const recovered = await retryAuth();
    if (!recovered) {
      await beginInteractiveLogin();
    }
    setRetryTrigger((prev) => prev + 1);
  }, [retryAuth, beginInteractiveLogin]);

  // Refresh function for when data needs to be reloaded (e.g., after patch apply failure)
  const refreshData = useCallback(() => {
    if (isApiReady) {
      setRetryTrigger((prev) => prev + 1);
    }
  }, [isApiReady]);

  const handleSchedulePatch = useCallback(
    (patch: SchedulePatch | null | undefined, source: "local" | "remote" = "local") => {
      if (!patch?.changes?.length) {
        if (source === "local") {
          refreshData();
        }
        return;
      }

      if (
        typeof patch.revision === "number" &&
        patch.revision > 0 &&
        source === "remote" &&
        patch.revision <= lastRevisionRef.current
      ) {
        return;
      }

      if (
        source === "remote" &&
        patch.client_mutation_id &&
        recentMutationIdsRef.current.includes(patch.client_mutation_id)
      ) {
        if (typeof patch.revision === "number" && patch.revision > 0) {
          lastRevisionRef.current = Math.max(
            lastRevisionRef.current,
            patch.revision,
          );
        }
        return;
      }

      if (patch.client_mutation_id) {
        trackMutationId(patch.client_mutation_id);
      }

      const visibleWeekKeys =
        source === "remote"
          ? new Set(weeks.map((week) => week.weekKey))
          : undefined;

      setEmployees((currentEmployees) => {
        const result = applySchedulePatch(
          currentEmployees,
          patch,
          visibleWeekKeys,
        );

        if (result.error) {
          queueMicrotask(() => refreshData());
          return currentEmployees;
        }

        if (result.applied) {
          if (typeof patch.revision === "number" && patch.revision > 0) {
            lastRevisionRef.current = Math.max(
              lastRevisionRef.current,
              patch.revision,
            );
          }
          // Remounting DataTable via dataVersion resets scroll. Capture now,
          // while the current wrapper is still mounted, for both local edits
          // and incoming websocket patches.
          captureDataTableScroll(
            wrapperRef.current,
            prevScrollTopRef,
            prevScrollLeftRef,
            needRestoreScrollRef,
          );
          queueMicrotask(() => {
            if (source === "remote") {
              pendingRemoteFlashChangesRef.current = patch.changes;
            }
            setDataVersion((version) => version + 1);
          });
          return result.employees;
        }

        return currentEmployees;
      });
    },
    [weeks, refreshData, trackMutationId],
  );

  const handleRemotePatch = useCallback(
    (patch: SchedulePatch) => {
      handleSchedulePatch(patch, "remote");
    },
    [handleSchedulePatch],
  );

  const catchUpSchedule = useCallback(
    async (opts?: { allowSnapshotFallback?: boolean }) => {
      if (!isApiReady || !hasSnapshotRef.current || catchingUpRef.current) {
        return;
      }

      catchingUpRef.current = true;
      const allowSnapshotFallback = opts?.allowSnapshotFallback !== false;
      try {
        const response = await apiService.getScheduleChanges({
          since: lastRevisionRef.current,
          year: currentYear,
          week: currentWeek,
          number: numberOfWeeks,
        });

        if (!response.success || !response.data) {
          if (allowSnapshotFallback) {
            refreshData();
          }
          return;
        }

        if (response.data.snapshot_required) {
          if (allowSnapshotFallback) {
            refreshData();
          }
          return;
        }

        for (const patch of response.data.patches ?? []) {
          handleSchedulePatch(patch, "remote");
        }

        if (response.data.revision > lastRevisionRef.current) {
          lastRevisionRef.current = response.data.revision;
        }
      } finally {
        catchingUpRef.current = false;
      }
    },
    [
      isApiReady,
      currentYear,
      currentWeek,
      numberOfWeeks,
      handleSchedulePatch,
      refreshData,
    ],
  );

  catchUpScheduleRef.current = catchUpSchedule;

  const catchUpOnReconnect = useCallback(() => {
    void catchUpSchedule({ allowSnapshotFallback: true });
  }, [catchUpSchedule]);

  const { status: realtimeStatus } = useScheduleRealtime(
    isApiReady,
    handleRemotePatch,
    catchUpOnReconnect,
  );

  const performDrop = useCallback(
    async (targetWeekKey: string) => {
      if (!dragData || dragData.sourceWeekKey === targetWeekKey) {
        setDragData(null);
        return;
      }

      try {
        const targetWeekInfo = weeks.find((w) => w.weekKey === targetWeekKey);
        if (targetWeekInfo && isWeekInPast(targetWeekInfo)) {
          setDragData(null);
          return;
        }

        const sourceEmployee = employees.find(
          (emp) => emp.id === dragData.employeeId,
        );
        const sourceProject = sourceEmployee?.projects.find(
          (p) => p.id === dragData.projectId,
        );

        if (!sourceProject) {
          console.error("Source project not found:", {
            employeeId: dragData.employeeId,
            projectId: dragData.projectId,
          });
          setDragData(null);
          return;
        }

        const planHours = dragData.hours || 0;
        const clientMutationId = crypto.randomUUID();
        trackMutationId(clientMutationId);
        const updateResponse = await apiService.updateEvent(
          {
            person: dragData.employeeId,
            hours: planHours,
            activity: sourceProject.activityId,
            week: Number(targetWeekKey),
            original_week: Number(dragData.sourceWeekKey),
            comment: dragData.comment || "",
          },
          clientMutationId,
        );

        if (!updateResponse.success) {
          console.error("Failed to update hours:", updateResponse.message);
          setDragData(null);
          return;
        }

        if (updateResponse.data) {
          handleSchedulePatch(
            {
              ...updateResponse.data,
              client_mutation_id:
                updateResponse.data.client_mutation_id ?? clientMutationId,
            },
            "local",
          );
        } else {
          refreshData();
        }
      } catch (error) {
        console.error("Error during drag and drop operation:", error);
      } finally {
        setDragData(null);
        try {
          document.body.classList.remove("dnd-active");
        } catch {}
      }
    },
    [
      dragData,
      employees,
      weeks,
      handleSchedulePatch,
      refreshData,
      trackMutationId,
    ],
  );

  // Navigation functions for SpeedDial
  const navigateFastBackward = () => {
    const currentStart = weekToDate(currentYear, currentWeek);
    const newStart = new Date(currentStart);
    newStart.setDate(newStart.getDate() - 7 * numberOfWeeks);
    const { weekNumber, year } = dateToWeek(newStart);

    setCurrentWeek(weekNumber);
    setCurrentYear(year);
    setRetryTrigger((prev) => prev + 1);
  };

  const navigateBackward = () => {
    const currentStart = weekToDate(currentYear, currentWeek);
    const newStart = new Date(currentStart);
    newStart.setDate(newStart.getDate() - 7);
    const { weekNumber, year } = dateToWeek(newStart);

    setCurrentWeek(weekNumber);
    setCurrentYear(year);
    setRetryTrigger((prev) => prev + 1);
  };

  const navigateForward = () => {
    const currentStart = weekToDate(currentYear, currentWeek);
    const newStart = new Date(currentStart);
    newStart.setDate(newStart.getDate() + 7);
    const { weekNumber, year } = dateToWeek(newStart);

    setCurrentWeek(weekNumber);
    setCurrentYear(year);
    setRetryTrigger((prev) => prev + 1);
  };

  const navigateFastForward = () => {
    const currentStart = weekToDate(currentYear, currentWeek);
    const newStart = new Date(currentStart);
    newStart.setDate(newStart.getDate() + 7 * numberOfWeeks);
    const { weekNumber, year } = dateToWeek(newStart);

    setCurrentWeek(weekNumber);
    setCurrentYear(year);
    setRetryTrigger((prev) => prev + 1);
  };

  // SpeedDial handlers
  const handleWeekRangeApply = (
    startWeek: number,
    startYear: number,
    weeks: number,
  ) => {
    setCurrentWeek(startWeek);
    setCurrentYear(startYear);
    setNumberOfWeeks(weeks);
    setRetryTrigger((prev) => prev + 1);
  };

  const handleRangeDelete = () => {
    // Patch application is handled via onSchedulePatch in RangeDeleteModal
  };

  // Effect to load schedule data (only when API is ready)
  useEffect(() => {
    // Don't load data if API is not ready (no token set)
    if (!isApiReady || !viewStateReady) {
      return;
    }

    const requestId = ++latestLoadRequestRef.current;
    let isCancelled = false;

    const loadData = async () => {
      // Capture current scroll position to restore after data refresh
      captureDataTableScroll(
        wrapperRef.current,
        prevScrollTopRef,
        prevScrollLeftRef,
        needRestoreScrollRef,
      );
      setLoading(true);
      setApiError(null); // Clear previous errors
      hasSnapshotRef.current = false;
      try {
        // Load data from API (token refresh is now handled automatically by the API service)
        const response = await apiService.getSchedule({
          year: currentYear,
          week: currentWeek,
          number: numberOfWeeks,
        });

        if (isCancelled || requestId !== latestLoadRequestRef.current) {
          return;
        }

        if (response.success && response.data) {
          // Transform API response to our internal format
          const transformedEmployees = transformApiResponse(response.data);
          setEmployees(transformedEmployees);
          setApiError(null); // Clear any previous errors
          if (
            typeof response.revision === "number" &&
            Number.isFinite(response.revision)
          ) {
            lastRevisionRef.current = response.revision;
          }
          hasSnapshotRef.current = true;
          void catchUpScheduleRef.current({ allowSnapshotFallback: false });
          // Bump version to ensure DataTable updates its internal view
          setDataVersion((v) => v + 1);
          setWeeks(
            generateWeekRange(currentYear, currentWeek, numberOfWeeks),
          );
        } else {
          console.error("Failed to load schedule data:", response.message);
          const isAuthFailure =
            /unauthorized|authentication token|session expired/i.test(
              response.message ?? "",
            );
          if (isAuthFailure) {
            // SessionExpiredModal / auth recovery banner handles re-login.
            setApiError(null);
          } else {
            setApiError(`Failed to load schedule data: ${response.message}`);
          }
        }
      } catch (error) {
        if (isCancelled || requestId !== latestLoadRequestRef.current) {
          return;
        }

        console.error("Error loading schedule data:", error);
        setApiError(
          `Network error: Unable to connect to the server. Please check your connection and try again.`,
        );
      } finally {
        if (!isCancelled && requestId === latestLoadRequestRef.current) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [
    isApiReady,
    viewStateReady,
    currentYear,
    currentWeek,
    numberOfWeeks,
    retryTrigger,
  ]);

  // Color coding function based on hours (0-40)
  const getHourColor = (hours: number): string => {
    if (hours < 0) return "var(--red-800)"; // red-800
    if (hours === 0) return "var(--red-500)"; // red-500
    if (hours <= 7) return "var(--red-300)"; // red-500
    if (hours <= 15) return "var(--orange-500)"; // orange-500
    if (hours <= 23) return "var(--yellow-500)"; // yellow-500
    if (hours <= 31) return "var(--teal-500)"; // teal-500
    return "var(--green-500)"; // green-500
  };

  // Format week tooltip
  const getWeekTooltip = (week: WeekInfo): string => {
    return formatWeekTooltip(week);
  };

  // Drag and drop handlers
  const handleDragStart = (
    e: React.DragEvent,
    projectId: string,
    employeeId: number,
    sourceWeekKey: string,
    hours: number,
    comment?: string,
  ) => {
    // Ensure native drag is properly initialized
    try {
      e.dataTransfer?.setData(
        "text/plain",
        JSON.stringify({
          type: "hours",
          projectId,
          employeeId,
          sourceWeekKey,
          hours,
        }),
      );
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = "move";
      }
    } catch {}

    const dragInfo = { projectId, employeeId, sourceWeekKey, hours, comment };
    setDragData(dragInfo);
    // Prevent child controls from intercepting dragover/drop during active drag
    try {
      document.body.classList.add("dnd-active");
    } catch {}
    // Yield once so useEffect(dragData) can run and mark drop zones before hover
    // Some browsers are sensitive to timing; this ensures zones appear instantly.
    setTimeout(() => {}, 0);
    // Do NOT force-remount during drag; it will kill dragend events.
  };

  // Determine if the given cell is a valid drop target based on dataset + current dragData
  const isValidCellTarget = (
    cell: HTMLElement,
  ): { valid: boolean; weekKey?: string } => {
    if (!dragData) return { valid: false };
    const empId = cell.getAttribute("data-emp-id");
    const projId = cell.getAttribute("data-proj-id");
    const weekKey = cell.getAttribute("data-week");

    if (!empId || !projId || !weekKey) return { valid: false };
    const isSamePair =
      String(dragData.employeeId) === empId && dragData.projectId === projId;
    if (!isSamePair) return { valid: false };
    const weekInfo = weeks.find((w) => w.weekKey === weekKey);
    if (!weekInfo || isWeekInPast(weekInfo)) return { valid: false };
    return { valid: true, weekKey };
  };

  const handleCellDragOver = (e: React.DragEvent) => {
    if (!dragData) return; // ignore when not actively dragging our hours
    const cell = e.currentTarget as HTMLElement;
    const valid =
      cell.classList.contains("is-drop-zone") || isValidCellTarget(cell).valid;
    if (valid) {
      // Must preventDefault to allow dropping
      e.preventDefault();
      try {
        document
          .querySelectorAll(".is-drag-over")
          .forEach((el) => el.classList.remove("is-drag-over"));
        cell.classList.add("is-drag-over");
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      } catch {}
    } else {
      try {
        cell.classList.remove("is-drag-over");
      } catch {}
    }
  };

  const handleCellDragEnter = (e: React.DragEvent) => {
    if (!dragData) return;
    const cell = e.currentTarget as HTMLElement;
    const valid =
      cell.classList.contains("is-drop-zone") || isValidCellTarget(cell).valid;
    if (valid) {
      e.preventDefault();
      try {
        cell.classList.add("is-drag-over");
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      } catch {}
    }
  };

  const handleCellDragLeave = (e: React.DragEvent) => {
    const cell = e.currentTarget as HTMLElement;
    try {
      cell.classList.remove("is-drag-over");
    } catch {}
  };

  const handleCellDrop = (e: React.DragEvent) => {
    const cell = e.currentTarget as HTMLElement;
    let { valid, weekKey } = isValidCellTarget(cell);
    if (!valid && cell.classList.contains("is-drop-zone")) {
      const wk = cell.getAttribute("data-week");
      if (wk) {
        weekKey = wk;
        valid = true;
      }
    }
    if (valid && weekKey) {
      handleDrop(e, weekKey);
    } else {
      // Prevent default to stop browser from navigating/opening data
      e.preventDefault();
      handleDragEnd();
    }
  };

  // Mark potential drop zones in the DOM without forcing React remounts
  useEffect(() => {
    // Clear previous markers first
    document
      .querySelectorAll(".is-drop-zone, .is-drag-over")
      .forEach((el) => el.classList.remove("is-drop-zone", "is-drag-over"));

    if (!dragData) return;

    try {
      // Document-level capture listeners to ensure DnD works within scrollable containers
      const onDocDragEnter = (evt: DragEvent) => {
        const target = evt.target as HTMLElement | null;
        const cell = target?.closest?.(
          '[data-schedule-cell="true"]',
        ) as HTMLElement | null;
        if (cell && cell.classList.contains("is-drop-zone")) {
          evt.preventDefault();
          cell.classList.add("is-drag-over");
        }
      };
      const onDocDragOver = (evt: DragEvent) => {
        const target = evt.target as HTMLElement | null;
        const cell = target?.closest?.(
          '[data-schedule-cell="true"]',
        ) as HTMLElement | null;
        // Always prevent default for marked zones so drop is allowed
        if (cell && cell.classList.contains("is-drop-zone")) {
          evt.preventDefault();
          try {
            document
              .querySelectorAll(".is-drag-over")
              .forEach((el) => el.classList.remove("is-drag-over"));
            cell.classList.add("is-drag-over");
          } catch {}
          if (evt.dataTransfer) evt.dataTransfer.dropEffect = "move";
        }
      };
      const onDocDragLeave = (evt: DragEvent) => {
        const target = evt.target as HTMLElement | null;
        const cell = target?.closest?.(
          '[data-schedule-cell="true"]',
        ) as HTMLElement | null;
        if (cell) {
          cell.classList.remove("is-drag-over");
        }
      };
      const onDocDrop = async (evt: DragEvent) => {
        const target = evt.target as HTMLElement | null;
        const cell = target?.closest?.(
          '[data-schedule-cell="true"]',
        ) as HTMLElement | null;
        if (cell && cell.classList.contains("is-drop-zone")) {
          evt.preventDefault();
          const weekKey = cell.getAttribute("data-week");
          if (weekKey) {
            await performDrop(weekKey);
            return;
          }
        }
        // Invalid drop: prevent default navigation and cleanup
        evt.preventDefault();
        handleDragEnd();
      };
      document.addEventListener("dragenter", onDocDragEnter, { capture: true });
      document.addEventListener("dragover", onDocDragOver, { capture: true });
      document.addEventListener("dragleave", onDocDragLeave, { capture: true });
      document.addEventListener("drop", onDocDrop, { capture: true });

      // Mark only the cells that match the current employee/project pair
      const selector = `*[data-schedule-cell="true"][data-emp-id="${dragData.employeeId}"][data-proj-id="${dragData.projectId}"]`;
      const cells = Array.from(
        document.querySelectorAll(selector),
      ) as HTMLElement[];

      let marked = 0;
      cells.forEach((cell) => {
        const weekKey = cell.getAttribute("data-week");
        if (!weekKey) return;
        // Skip the source week; you cannot drop onto the same cell
        if (dragData && weekKey === dragData.sourceWeekKey) return;
        const isPast = weeks.some(
          (w) => w.weekKey === weekKey && isWeekInPast(w),
        );
        if (!isPast) {
          cell.classList.add("is-drop-zone");
          marked++;
        }
      });
      if (marked === 0) {
        // No zones: remove active flag to avoid blocking interactions
        document.body.classList.remove("dnd-active");
      }

      // Cleanup global listeners when drag ends or deps change
      return () => {
        document.removeEventListener(
          "dragenter",
          onDocDragEnter as any,
          { capture: true } as any,
        );
        document.removeEventListener(
          "dragover",
          onDocDragOver as any,
          { capture: true } as any,
        );
        document.removeEventListener(
          "dragleave",
          onDocDragLeave as any,
          { capture: true } as any,
        );
        document.removeEventListener(
          "drop",
          onDocDrop as any,
          { capture: true } as any,
        );
      };
    } catch {}
  }, [dragData, weeks, handleDragEnd, performDrop]);

  const handleDrop = async (e: React.DragEvent, targetWeekKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    await performDrop(targetWeekKey);
  };

  // Check if employee has non-working hours in a given week
  const hasNonWorkingInWeek = (
    employee: Employee,
    weekKey: string,
  ): boolean => {
    return employee.projects.some(
      (project) =>
        project.hourType === "non_working" &&
        project.reference !== "0xFF-SP-002" && // filter sickness
        (project.weeklyHours[weekKey] || 0) > 0,
    );
  };

  const EmptyCellPlaceholder: React.FC<{ onClick: () => void }> = ({
    onClick,
  }) => (
    <button
      type="button"
      className="schedule-cell-placeholder"
      onClick={onClick}
      aria-label="Add planning"
    >
      <i className="pi pi-plus" />
    </button>
  );

  // Hour button component
  const HourButton: React.FC<{
    hours: number;
    isAvailability?: boolean;
    comment?: string;
    hasNonWorking?: boolean;
    isReservation?: boolean;
    isPast?: boolean;
    onClick?: () => void;
    draggable?: boolean;
    onDragStart?: (event: React.DragEvent) => void;
    onDragEnd?: (event: React.DragEvent) => void;
  }> = ({
    hours,
    isAvailability = false,
    comment,
    hasNonWorking = false,
    isReservation = false,
    isPast = false,
    onClick,
    draggable = false,
    onDragStart,
    onDragEnd,
  }) => {
    const color = getHourColor(hours);
    const isClickable = onClick && !isPast;

    // Append || Reservation to comment if it's a reservation
    let displayComment = comment;
    if (isReservation) {
      displayComment =
        comment && comment.trim().length > 0
          ? `${comment} || Reservation`
          : "Reservation";
    }

    const hasComment = displayComment && displayComment.trim().length > 0;
    const hasActualComment = comment && comment.trim().length > 0;

    return (
      <div
        style={{ position: "relative", display: "inline-block" }}
        className={`${hasComment ? "hour-button-with-comment" : ""} ${
          isPast ? "past-week-cell" : ""
        }`}
        data-pr-tooltip={hasComment ? displayComment : undefined}
        data-pr-position="top"
      >
        <Button
          label={hours.toString()}
          className={`p-button-sm ${isPast ? "past-week-button" : ""}`}
          style={{
            backgroundColor: isPast ? "var(--surface-300)" : color,
            borderColor: isPast ? "var(--surface-400)" : color,
            color: isPast ? "var(--text-color-secondary)" : "white",
            width: "55px",
            minWidth: "40px",
            height: "32px",
            cursor: isClickable
              ? "pointer"
              : draggable
                ? "grab"
                : "not-allowed",
          }}
          size="small"
          onClick={isClickable ? onClick : undefined}
          disabled={!isClickable && !draggable}
          draggable={draggable && !isPast}
          onDragStart={draggable && !isPast ? onDragStart : undefined}
          onDragEnd={draggable && !isPast ? onDragEnd : undefined}
        />
        {hasActualComment && <span className="comment-indicator">💬</span>}
        {hasNonWorking && <span className="non-working-indicator">⛱️</span>}
        {isReservation && <span className="reservation-indicator">🚧</span>}
      </div>
    );
  };

  // Modal handlers
  const openSchedulingModal = (initialData?: any) => {
    setModalInitialData(initialData || null);
    setSchedulingModalVisible(true);
  };

  const closeSchedulingModal = () => {
    setSchedulingModalVisible(false);
    setModalInitialData(null);
  };

  // Click handlers for different types of hour buttons
  const handleAvailabilityClick = (employee: Employee, week: WeekInfo) => {
    // This is for creating new hours (isExisting = false)
    openSchedulingModal({
      employeeIds: [employee.id],
      weekNumber: week.weekNumber,
      year: week.year,
      hours: employee.availability[week.weekKey] || 8,
      isExisting: false, // Flag to indicate this is a new entry
    });
  };

  const handleProjectHoursClick = (
    project: Project,
    employee: Employee,
    week: WeekInfo,
  ) => {
    // This is for editing existing hours (isExisting = true)
    openSchedulingModal({
      projectId: project.id,
      employeeIds: [employee.id],
      weekNumber: week.weekNumber,
      year: week.year,
      hours: project.weeklyHours[week.weekKey] || 8,
      comment: project.comments[week.weekKey] || "",
      entryId: `${project.id}-${employee.id}-${week.weekKey}`, // Generate a unique entry ID
      activityId: project.activityId,
      originalWeekNumber: week.weekNumber,
      originalYear: week.year,
      isExisting: true, // Flag to indicate this is an existing entry
    });
  };

  const handleProjectSummaryClick = (
    projectSummary: ProjectSummary,
    week: WeekInfo,
  ) => {
    // This is for creating new hours (isExisting = false)
    openSchedulingModal({
      projectId: projectSummary.id,
      employeeIds: projectSummary.employees.map((emp) => emp.id),
      weekNumber: week.weekNumber,
      year: week.year,
      hours: Math.round(
        (projectSummary.totalWeeklyHours[week.weekKey] || 0) /
          projectSummary.employees.length,
      ),
      activityId: projectSummary.activityId,
      isExisting: false, // Flag to indicate this is a new entry
    });
  };

  const handleEmptyCellClick = (rowData: any, week: WeekInfo) => {
    if (rowData.isProject) {
      const project = rowData.project as Project;
      const employee = employees.find(
        (emp) => emp.id === rowData.parentEmployeeId,
      )!;
      openSchedulingModal({
        projectId: project.id,
        employeeIds: [rowData.parentEmployeeId],
        weekNumber: week.weekNumber,
        year: week.year,
        hours: employee.availability[week.weekKey] || 8,
        activityId: project.activityId,
        isExisting: false,
      });
    } else if (rowData.isProjectEmployee) {
      const projectEmployee = rowData.projectEmployee as ProjectEmployee;
      const employee = employees.find((emp) => emp.id === projectEmployee.id)!;
      openSchedulingModal({
        projectId: rowData.parentProjectId,
        employeeIds: [projectEmployee.id],
        weekNumber: week.weekNumber,
        year: week.year,
        hours: employee.availability[week.weekKey] || 8,
        activityId: rowData.projectSummary?.activityId,
        isExisting: false,
      });
    } else if (rowData.isProjectSummary) {
      handleProjectSummaryClick(rowData.projectSummary as ProjectSummary, week);
    }
  };

  // Range delete modal handlers
  const openRangeDeleteModal = (initialData?: any) => {
    setRangeDeleteInitialData(initialData || null);
    setRangeDeleteModalVisible(true);
  };

  const closeRangeDeleteModal = () => {
    setRangeDeleteModalVisible(false);
    setRangeDeleteInitialData(null);
  };

  // Helper function to find earliest and latest weeks for a project-employee combination
  const getProjectEmployeeWeekRange = (
    project: Project,
    employee: Employee,
  ) => {
    const weekKeys = Object.keys(project.weeklyHours)
      .filter((weekKey) => (project.weeklyHours[weekKey] || 0) > 0)
      .sort();

    if (weekKeys.length === 0) return null;

    const startWeekKey = weekKeys[0];
    const endWeekKey = weekKeys[weekKeys.length - 1];

    const startYear = parseInt(startWeekKey.substring(0, 4));
    const startWeek = parseInt(startWeekKey.substring(4, 6));

    const endYear = parseInt(endWeekKey.substring(0, 4));
    const endWeek = parseInt(endWeekKey.substring(4, 6));

    return {
      startWeek,
      endWeek,
      startYear,
      endYear,
    };
  };

  // Handle range delete for specific project-employee combination
  const handleProjectEmployeeRangeDelete = (
    project: Project,
    employee: Employee,
  ) => {
    const weekRange = getProjectEmployeeWeekRange(project, employee);

    openRangeDeleteModal({
      projectId: project.id,
      activityId: project.activityId,
      employeeIds: [employee.id],
      startWeek: weekRange?.startWeek,
      startYear: weekRange?.startYear,
      endWeek: weekRange?.endWeek,
      endYear: weekRange?.endYear,
      disableProjectSelection: true,
      disableEmployeeSelection: true,
    });
  };

  // Generate week columns dynamically
  const generateWeekColumns = () => {
    return weeks.map((week) => {
      const fieldName = `week_${week.weekKey}`;
      const isPast = isWeekInPast(week);
      return (
        <Column
          key={week.weekKey}
          field={fieldName}
          align={"center"}
          className={isPast ? "past-week-column" : ""}
          header={
            <div>
              <span
                className={`week-header ${isPast ? "past-week-header" : ""}`}
                data-pr-tooltip={getWeekTooltip(week)}
                data-pr-position="top"
                data-pr-at="center top"
                data-pr-my="center bottom"
              >
                W{week.weekNumber}
              </span>
            </div>
          }
          body={(rowData: any) => {
            let cellContent = null;
            let isEmptyCell = false;

            if (viewMode === "employee") {
              // Employee view logic
              if (rowData.isProject) {
                // Project row under employee
                const project = rowData.project as Project;
                const hours = project.weeklyHours[week.weekKey] || 0;
                const comment = project.comments[week.weekKey];

                if (hours > 0) {
                  cellContent = (
                    <HourButton
                      hours={hours}
                      comment={comment}
                      hasNonWorking={project.hourType === "non_working"}
                      isReservation={project.isReservation}
                      isPast={isPast}
                      draggable={!isPast}
                      onDragStart={(event) => {
                        handleDragStart(
                          event,
                          project.id,
                          rowData.parentEmployeeId,
                          week.weekKey,
                          hours,
                          comment || "",
                        );
                      }}
                      onDragEnd={handleDragEnd}
                      onClick={() =>
                        handleProjectHoursClick(
                          project,
                          // Find the parent employee for this project
                          employees.find(
                            (emp) => emp.id === rowData.parentEmployeeId,
                          )!,
                          week,
                        )
                      }
                    />
                  );
                } else if (!isPast) {
                  cellContent = (
                    <EmptyCellPlaceholder
                      onClick={() => handleEmptyCellClick(rowData, week)}
                    />
                  );
                  isEmptyCell = true;
                }
              } else if (rowData.isEmployee) {
                // Employee availability row
                const employee = employees.find(
                  (emp) => emp.id === rowData.id,
                )!;
                const hours = rowData.availability[week.weekKey] || 0;
                const hasNonWorking = hasNonWorkingInWeek(
                  employee,
                  week.weekKey,
                );
                cellContent = (
                  <HourButton
                    hours={hours}
                    isAvailability={true}
                    hasNonWorking={hasNonWorking}
                    isPast={isPast}
                    onClick={() => handleAvailabilityClick(employee, week)}
                  />
                );
              }
            } else {
              // Project view logic
              if (rowData.isProjectSummary) {
                // Project summary row
                const projectSummary = rowData.projectSummary as ProjectSummary;
                const totalHours =
                  projectSummary.totalWeeklyHours[week.weekKey] || 0;
                if (totalHours > 0) {
                  cellContent = (
                    <HourButton
                      hours={totalHours}
                      isPast={isPast}
                      onClick={() =>
                        handleProjectSummaryClick(projectSummary, week)
                      }
                    />
                  );
                } else if (!isPast) {
                  cellContent = (
                    <EmptyCellPlaceholder
                      onClick={() => handleEmptyCellClick(rowData, week)}
                    />
                  );
                  isEmptyCell = true;
                }
              } else if (rowData.isProjectEmployee) {
                // Employee row under project
                const projectEmployee =
                  rowData.projectEmployee as ProjectEmployee;
                const hours = projectEmployee.weeklyHours[week.weekKey] || 0;
                const comment = projectEmployee.comments[week.weekKey];

                if (hours > 0) {
                  cellContent = (
                    <HourButton
                      hours={hours}
                      comment={comment}
                      hasNonWorking={
                        rowData.projectSummary?.hourType === "non_working"
                      }
                      isReservation={projectEmployee.isReservation}
                      isPast={isPast}
                      draggable={!isPast}
                      onDragStart={(event) => {
                        handleDragStart(
                          event,
                          rowData.parentProjectId,
                          projectEmployee.id,
                          week.weekKey,
                          hours,
                          comment || "",
                        );
                      }}
                      onDragEnd={handleDragEnd}
                      onClick={() => {
                        // Create a project object from the projectSummary
                        const project: Project = {
                          id: rowData.parentProjectId,
                          activityId: rowData.projectSummary?.activityId || 0,
                          name: rowData.projectSummary?.name || "",
                          activity: rowData.projectSummary?.activity || "",
                          client: rowData.projectSummary?.client || "",
                          project: rowData.projectSummary?.project || "",
                          hourType:
                            rowData.projectSummary?.hourType || "billable",
                          reference: rowData.projectSummary?.reference || "",
                          weeklyHours: { [week.weekKey]: hours },
                          comments: { [week.weekKey]: comment || "" },
                          isReservation: projectEmployee.isReservation,
                        };

                        // Find the full employee object using the projectEmployee ID
                        const employee = employees.find(
                          (emp) => emp.id === projectEmployee.id,
                        )!;

                        handleProjectHoursClick(project, employee, week);
                      }}
                    />
                  );
                } else if (!isPast) {
                  cellContent = (
                    <EmptyCellPlaceholder
                      onClick={() => handleEmptyCellClick(rowData, week)}
                    />
                  );
                  isEmptyCell = true;
                }
              }
            }

            // Wrap content in drop zone for project rows in both views
            // Debug: Log for first few cells when dragging (check more weeks)
            if (dragData && (rowData.isProject || rowData.isProjectEmployee)) {
              // Removed verbose logging - keeping only essential debugging
            }

            const isDragTarget =
              dragData &&
              ((rowData.isProjectEmployee &&
                dragData.projectId === rowData.parentProjectId &&
                dragData.employeeId === rowData.projectEmployee?.id) ||
                (rowData.isProject &&
                  dragData.projectId === rowData.project?.id &&
                  dragData.employeeId === rowData.parentEmployeeId));
            const isValidDropTarget = isDragTarget && !isPast;

            const cellClassName = [
              "schedule-cell",
              isEmptyCell
                ? "schedule-cell--empty"
                : cellContent
                  ? "schedule-cell--filled"
                  : "",
              isPast ? "schedule-cell--past" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <div
                className={cellClassName}
                style={{
                  textAlign: "center",
                  minWidth: "60px",
                  minHeight: "40px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                data-schedule-cell="true"
                data-week={week.weekKey}
                data-emp-id={
                  rowData.isEmployee
                    ? rowData.id
                    : rowData.isProjectEmployee
                      ? rowData.projectEmployee?.id
                      : rowData.parentEmployeeId
                }
                data-proj-id={
                  rowData.isProject
                    ? rowData.project?.id
                    : rowData.parentProjectId
                }
                data-availability={rowData.isEmployee ? "true" : undefined}
                data-drop-zone={isValidDropTarget ? "true" : "false"}
                onDragEnter={handleCellDragEnter}
                onDragOver={handleCellDragOver}
                onDragLeave={handleCellDragLeave}
                onDrop={handleCellDrop}
              >
                {cellContent}
              </div>
            );
          }}
        />
      );
    });
  };

  // Memoize the transformed data to prevent unnecessary re-renders
  const tableData = useMemo(() => {
    const result: any[] = [];

    // Helper function to check if an item matches the search term
    const matchesSearch = (text: string): boolean => {
      if (!searchTerm.trim()) return true;
      return text.toLowerCase().includes(searchTerm.toLowerCase().trim());
    };

    if (viewMode === "employee") {
      // Employee view - sort employees, but keep projects grouped under their employee
      // Apply active filter first
      let filteredEmployees = applyEmployeeFilter(employees, activeFilter);

      const sortedEmployees = [...filteredEmployees].sort((a, b) => {
        if (sortField === "name") {
          return a.name.localeCompare(b.name) * sortOrder;
        }
        return 0;
      });

      sortedEmployees.forEach((employee) => {
        // Check if employee matches search or any of their projects match
        const employeeMatches = matchesSearch(employee.name);
        const projectMatches = employee.projects.some((project: Project) =>
          matchesSearch(
            `${project.client} ${project.project} ${project.name} ${project.activity} ${project.reference}`,
          ),
        );

        // Only show employee if they or their projects match the search
        if (employeeMatches || projectMatches) {
          // Add employee row
          result.push({
            id: employee.id,
            name: employee.name,
            sortKey: employee.name.toLowerCase(), // For consistent sorting
            availability: employee.availability,
            projects: employee.projects,
            isProject: false,
            isEmployee: true,
            isExpanded: expandedRows[employee.id] === true,
          });

          // Add project rows if employee is expanded (sort by client, then project)
          if (expandedRows[employee.id] === true) {
            const sortedProjects = [...employee.projects].sort((a, b) => {
              const clientCompare = a.client.localeCompare(b.client);
              if (clientCompare !== 0) return clientCompare;
              return a.project.localeCompare(b.project);
            });

            sortedProjects.forEach((project: Project) => {
              // Only show projects that match the search (or show all if employee name matches)
              const projectSearchText = `${project.client} ${project.project} ${project.name} ${project.activity} ${project.reference}`;
              if (employeeMatches || matchesSearch(projectSearchText)) {
                result.push({
                  id: `${employee.id}-${project.id}`,
                  name: project.name,
                  sortKey: project.name.toLowerCase(),
                  project: project,
                  parentEmployeeId: employee.id,
                  isProject: true,
                  isEmployee: false,
                });
              }
            });
          }
        }
      });
    } else {
      // Project view - sort projects, but keep employees grouped under their project
      // Apply active filter to employees first
      const filteredEmployees = applyEmployeeFilter(employees, activeFilter);
      const projectSummaries = transformToProjectView(filteredEmployees);
      const sortedProjectSummaries = [...projectSummaries].sort((a, b) => {
        if (sortField === "name") {
          return a.name.localeCompare(b.name) * sortOrder;
        }
        return 0;
      });

      sortedProjectSummaries.forEach((projectSummary) => {
        // Check if project matches search or any of their employees match
        const projectSearchText = `${projectSummary.client} ${projectSummary.project} ${projectSummary.name} ${projectSummary.activity}`;
        const projectMatches = matchesSearch(projectSearchText);
        const employeeMatches = projectSummary.employees.some((employee) =>
          matchesSearch(employee.name),
        );

        // Only show project if it or its employees match the search
        if (projectMatches || employeeMatches) {
          // Add project summary row
          result.push({
            id: projectSummary.id,
            name: projectSummary.name,
            sortKey: projectSummary.name.toLowerCase(),
            projectSummary: projectSummary,
            isProject: false,
            isEmployee: false,
            isProjectSummary: true,
            isExpanded: expandedRows[projectSummary.id] === true,
          });

          // Add employee rows if project is expanded (sort by name, then surname)
          if (expandedRows[projectSummary.id] === true) {
            const sortedEmployees = [...projectSummary.employees].sort(
              (a, b) => {
                return a.name.localeCompare(b.name);
              },
            );

            sortedEmployees.forEach((employee) => {
              // Only show employees that match the search (or show all if project matches)
              if (projectMatches || matchesSearch(employee.name)) {
                result.push({
                  id: `${projectSummary.id}-${employee.id}`,
                  name: employee.name,
                  sortKey: employee.name.toLowerCase(),
                  projectEmployee: employee,
                  projectSummary,
                  parentProjectId: projectSummary.id,
                  isProject: false,
                  isEmployee: false,
                  isProjectEmployee: true,
                });
              }
            });
          }
        }
      });
    }

    return result;
  }, [
    employees,
    expandedRows,
    viewMode,
    sortField,
    sortOrder,
    searchTerm,
    activeFilter,
  ]);

  // Restore scroll after in-place tableData updates. Keep the pending flag so
  // a following DataTable remount (key={dataVersion}) can restore again —
  // React may flush the employees update before the dataVersion remount.
  useLayoutEffect(() => {
    if (!needRestoreScrollRef.current) return;
    restoreDataTableScroll(
      wrapperRef.current,
      prevScrollTopRef,
      prevScrollLeftRef,
    );
  }, [tableData]);

  // Restore after DataTable remount, then consume the pending restore.
  useLayoutEffect(() => {
    if (!needRestoreScrollRef.current) return;
    restoreDataTableScroll(
      wrapperRef.current,
      prevScrollTopRef,
      prevScrollLeftRef,
    );
    needRestoreScrollRef.current = false;
  }, [dataVersion]);

  useLayoutEffect(() => {
    const changes = pendingRemoteFlashChangesRef.current;
    if (!changes?.length) {
      return;
    }
    pendingRemoteFlashChangesRef.current = null;

    const wrapper = wrapperRef.current?.querySelector(
      ".p-datatable-wrapper",
    ) as HTMLElement | null;
    if (!wrapper) {
      return;
    }

    return flashVisibleScheduleCells(wrapper, changes);
  }, [dataVersion]);

  // Custom row class to style project rows differently
  const rowClassName = (data: any) => {
    if (data.isProject || data.isProjectEmployee) return "project-row";
    return "";
  };

  // Custom name column body template
  const nameBodyTemplate = (rowData: any) => {
    const handleExpand = (e: React.MouseEvent) => {
      if (rowData.isEmployee || rowData.isProjectSummary) {
        e.preventDefault();
        e.stopPropagation();

        setExpandedRows((prevState: any) => {
          const newState = { ...prevState };
          const currentlyExpanded = newState[rowData.id] === true;
          newState[rowData.id] = !currentlyExpanded;
          return newState;
        });
      }
    };

    const isExpandable = rowData.isEmployee || rowData.isProjectSummary;

    if (rowData.isProject) {
      // Employee view - project under employee
      const project = rowData.project as Project;
      return (
        <div
          style={{
            paddingLeft: "0rem",
            fontStyle: "italic",
            color: "var(--text-color-secondary)",
          }}
        >
          <div>
            {project.client} • {project.project}{" "}
            {project.isReservation ? "🚧" : ""}
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-color-secondary)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <ReferenceBadge value={project.reference} size="normal" />
            <ActivityBadge
              value={project.activity}
              hourType={project.hourType}
              size="normal"
            />
          </div>
        </div>
      );
    } else if (rowData.isProjectEmployee) {
      // Project view - employee under project
      const projectEmployee = rowData.projectEmployee as ProjectEmployee;
      const activeRemarks = getRemarksForView(
        projectEmployee.scheduleRemarks,
        weeks
      );

      return (
        <div
          className="flex align-items-center gap-2 flex-nowrap"
          style={{
            paddingLeft: "0rem",
            fontStyle: "italic",
            color: "var(--text-color-secondary)",
          }}
        >
          <span className="white-space-nowrap">{rowData.name}</span>
          {activeRemarks.length > 0 && (
            <ScheduleRemarkChips
              remarks={activeRemarks}
              idPrefix={`project-employee-${rowData.id}`}
            />
          )}
        </div>
      );
    } else if (rowData.isProjectSummary) {
      // Project view - project summary row
      const projectSummary = rowData.projectSummary as ProjectSummary;
      return (
        <div
          onClick={handleExpand}
          style={{
            cursor: "pointer",
          }}
        >
          <strong>
            {projectSummary.client} • {projectSummary.project}{" "}
            {projectSummary.isReservation ? "🚧" : ""}
          </strong>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-color-secondary)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <ReferenceBadge value={projectSummary.reference} size="normal" />
            <ActivityBadge
              value={projectSummary.activity}
              hourType={projectSummary.hourType}
              size="normal"
            />
          </div>
        </div>
      );
    }
    // Employee view - employee row
    const employee = employees.find((emp) => emp.id === rowData.id);
    const activeRemarks = employee
      ? getRemarksForView(employee.scheduleRemarks, weeks)
      : [];

    return (
      <div
        onClick={handleExpand}
        className="flex align-items-center gap-2 flex-nowrap"
        style={{
          cursor: isExpandable ? "pointer" : "default",
        }}
      >
        <strong className="white-space-nowrap">{rowData.name}</strong>
        {activeRemarks.length > 0 && (
          <ScheduleRemarkChips
            remarks={activeRemarks}
            idPrefix={`employee-${rowData.id}`}
          />
        )}
      </div>
    );
  };

  // Handle sorting
  const handleSort = () => {
    // Toggle sort order if same field, otherwise reset to ascending
    if (sortField === "name") {
      setSortOrder(sortOrder === 1 ? -1 : 1);
    } else {
      setSortField("name");
      setSortOrder(1);
    }
  };

  const recoveryMessage =
    apiError ||
    (!isApiReady && hasAttemptedAuth && !authLoading
      ? authError || "Your session has expired. Sign in again to continue."
      : null);

  return (
    <div className="schedule-page-container">
      <Tooltip
        key={`week-${dataVersion}-${weeks.length}`}
        target=".week-header"
      />
      <Tooltip key={`sort-${renderKey}`} target=".sort-header" />
      <Tooltip
        target=".speeddial-bottom-right .p-speeddial-action"
        position="top"
      />
      <Tooltip
        key={`comment-${tooltipRefreshKey}`}
        target=".hour-button-with-comment"
        position="top"
        content=""
        autoHide={false}
        hideDelay={300}
      />

      <style>
        {`
          
          .project-row {
            background-color: var(--surface-100) !important;
          }
          .project-row:hover {
            background-color: var(--surface-200) !important;
          }
          .comment-indicator {
            position: absolute;
            top: -8px;
            right: -8px;
            font-size: 16px;
            opacity: 0.8;
            z-index: 1;
          }
          .non-working-indicator {
            position: absolute;
            top: -12px;
            right: -12px;
            font-size: 16px;
            // opacity: 0.8;
            z-index: 1;
          }
          .reservation-indicator {
            position: absolute;
            bottom: -8px;
            right: -8px;
            font-size: 16px;
            opacity: 0.8;
            z-index: 1;
          }
          .week-header {
            cursor: help;
          }
          .schedule-remark-chip {
            font-size: 0.7rem;
            line-height: 1.2;
            padding: 0.15rem 0.45rem;
            background: var(--surface-100) !important;
            color: var(--text-color-secondary) !important;
            border: 1px solid var(--surface-300);
          }
          .schedule-remark-chip .p-tag-icon {
            font-size: 0.65rem;
            margin-right: 0.25rem;
            color: inherit;
          }
          .schedule-remark-chip--off {
            background: var(--surface-100) !important;
            color: var(--red-700) !important;
            border-color: var(--red-200);
          }
          .schedule-remark-chip--off .p-tag-icon {
            color: var(--red-600);
          }
          .schedule-remark-chip--constraint {
            background: var(--surface-100) !important;
            color: var(--orange-700) !important;
            border-color: var(--orange-200);
          }
          .schedule-remark-chip--constraint .p-tag-icon {
            color: var(--orange-600);
          }
          .schedule-remark-chip--note {
            background: var(--surface-100) !important;
            color: var(--blue-700) !important;
            border-color: var(--blue-200);
          }
          .schedule-remark-chip--note .p-tag-icon {
            color: var(--blue-600);
          }
          .sort-header {
            user-select: none;
          }
          /* Past week styling with diagonal stripes */
          .past-week-cell {
            position: relative;
          }
          .past-week-cell::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 4px,
              var(--surface-200) 4px,
              var(--surface-200) 8px
            );
            pointer-events: none;
            z-index: 0;
            border-radius: 4px;
          }
          .past-week-button {
            position: relative;
            z-index: 1;
            opacity: 0.8;
          }
          .past-week-button:hover {
            opacity: 0.7 !important;
          }
          .past-week-column {
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 3px,
              var(--surface-200) 3px,
              var(--surface-200) 6px
            ) !important;
          }
          .past-week-header {
            opacity: 0.6;
            color: var(--text-color-secondary);
          }
          /* Sticky header for DataTable */
          .p-datatable-scrollable .p-datatable-thead > tr > th {
            position: sticky !important;
            top: 0 !important;
            z-index: 100 !important;
            background: var(--surface-0) !important;
            box-shadow: 0 2px 4px var(--surface-300) !important;
          }
          .p-datatable-scrollable .p-datatable-wrapper {
            overflow-y: auto !important;
          }
          .p-datatable-scrollable .p-datatable-thead {
            position: sticky !important;
            top: 0 !important;
            z-index: 100 !important;
            background: var(--surface-0) !important;
          }
          /* Ensure tooltips appear above sticky header */
          .p-tooltip {
            z-index: 9999 !important;
          }
          .p-tooltip .p-tooltip-text {
            background: var(--surface-900) !important;
            color: var(--surface-0) !important;
            padding: 0.5rem !important;
            border-radius: 4px !important;
            font-size: 0.875rem !important;
            max-width: 300px !important;
            word-wrap: break-word !important;
          }
          /* During active drag, let drop-zone cell capture events; prevent its child controls from hijacking */
          body.dnd-active [data-schedule-cell="true"].is-drop-zone * {
            pointer-events: none !important;
          }
          /* Drop zone highlighting without React rerenders */
          /* Default drop zones: blue dashed */
          div[data-schedule-cell="true"].is-drop-zone {
            background-color: rgba(30, 144, 255, 0.10) !important; /* dodgerblue faint */
            border: 2px dashed rgba(30, 144, 255, 0.9) !important; /* blue dashed */
          }
          /* Hovered drop zone: green dashed */
          div[data-schedule-cell="true"].is-drop-zone.is-drag-over {
            background-color: rgba(50, 205, 50, 0.12) !important; /* limegreen faint */
            border: 3px dashed rgba(50, 205, 50, 1) !important; /* green dashed */
          }
          /* Empty cell add placeholder */
          .schedule-cell-placeholder {
            opacity: 0;
            width: 100%;
            min-height: 36px;
            border: 2px dashed transparent;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            border-radius: 4px;
            color: rgba(30, 144, 255, 0.9);
          }
          .schedule-cell--empty:not(.schedule-cell--past):hover .schedule-cell-placeholder {
            opacity: 1;
            border-color: rgba(30, 144, 255, 0.9);
            background-color: rgba(30, 144, 255, 0.10);
          }
          /* Filled cell hover */
          .schedule-cell--filled:not(.schedule-cell--past):hover:not(.is-drop-zone) {
            border: 2px dashed rgba(30, 144, 255, 0.5);
            background-color: rgba(30, 144, 255, 0.05);
          }
          @keyframes schedule-cell-flash {
            0% {
              box-shadow: inset 0 0 0 2px rgba(30, 144, 255, 0.9);
              background-color: rgba(30, 144, 255, 0.22);
            }
            100% {
              box-shadow: inset 0 0 0 2px transparent;
              background-color: transparent;
            }
          }
          .schedule-cell--flash {
            animation: schedule-cell-flash ${SCHEDULE_CELL_FLASH_MS}ms ease-out;
          }
          @media (prefers-reduced-motion: reduce) {
            .schedule-cell--flash {
              animation: none;
            }
          }
          /* Suppress hover affordances during drag */
          body.dnd-active .schedule-cell-placeholder {
            display: none;
          }
          body.dnd-active .schedule-cell--filled:hover:not(.is-drop-zone) {
            border: none;
            background: transparent;
          }
        `}
      </style>

      {/* <div className="schedule-page-container"> */}
      <div className="sticky-menu-bar">
        <div className="flex align-items-center justify-content-between">
          <div className="flex align-items-center gap-2">
            <ButtonGroup>
              <Button
                label="Employee View"
                icon="pi pi-users"
                className={`p-button-sm ${
                  viewMode === "employee" ? "" : "p-button-outlined"
                }`}
                onClick={() => {
                  if (viewMode !== "employee") {
                    setViewMode("employee");
                    setExpandedRows({}); // Clear expanded state when switching views
                    setSortField("name"); // Reset sorting
                    setSortOrder(1);
                    setRenderKey((prev) => prev + 1); // Force DataTable re-render for view mode change
                  }
                }}
              />
              <Button
                label="Project View"
                icon="pi pi-briefcase"
                className={`p-button-sm ${
                  viewMode === "project" ? "" : "p-button-outlined"
                }`}
                onClick={() => {
                  if (viewMode !== "project") {
                    setViewMode("project");
                    setExpandedRows({}); // Clear expanded state when switching views
                    setSortField("name"); // Reset sorting
                    setSortOrder(1);
                    setRenderKey((prev) => prev + 1); // Force DataTable re-render for view mode change
                  }
                }}
              />
            </ButtonGroup>
          </div>

          <div className="flex align-items-center gap-2">
            <LiveStatusIndicator status={realtimeStatus} />
            <FilterManager
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />
          </div>
        </div>
      </div>

      {recoveryMessage && (
        <div className="flex align-items-center gap-2 px-3 py-2">
          <Message severity="error" className="flex-1" text={recoveryMessage} />
          <Button
            label="Retry"
            icon="pi pi-refresh"
            className="p-button-sm p-button-outlined"
            onClick={retryLoadData}
            disabled={loading || authLoading}
          />
        </div>
      )}

      <div className="schedule-table-container">
        <div className="schedule-datatable-wrapper" ref={wrapperRef}>
          <DataTable
            key={`dt-${dataVersion}`}
            value={tableData}
            loading={
              loading ||
              ((authLoading || !isApiReady) &&
                employees.length === 0 &&
                !apiError)
            }
            dataKey="id"
            className="p-datatable-sm"
            scrollable
            scrollHeight="calc(100vh)"
            emptyMessage={
              apiError ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#dc2626",
                  }}
                >
                  <i
                    className="pi pi-exclamation-triangle"
                    style={{ fontSize: "2rem", marginBottom: "1rem" }}
                  ></i>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Unable to Load Schedule Data
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      opacity: 0.8,
                      marginBottom: "1rem",
                    }}
                  >
                    {apiError}
                  </div>
                  <Button
                    label="Retry"
                    icon="pi pi-refresh"
                    className="p-button-sm p-button-outlined"
                    onClick={retryLoadData}
                    disabled={loading || authLoading}
                  />
                </div>
              ) : authError ? (
                `Authentication error: ${authError}`
              ) : !isApiReady ? (
                "Initializing authentication..."
              ) : (
                "No schedule data available"
              )
            }
            rowClassName={rowClassName}
          >
            <Column
              style={{ width: "4rem" }}
              body={(rowData) => {
                if (rowData.isEmployee || rowData.isProjectSummary) {
                  const isExpanded = rowData.isExpanded;
                  return (
                    <Button
                      key={`expander-${rowData.id}`}
                      icon={
                        isExpanded
                          ? "pi pi-chevron-down"
                          : "pi pi-chevron-right"
                      }
                      className="p-button-text p-button-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        setExpandedRows((prevState: any) => {
                          const newState = { ...prevState };
                          const currentlyExpanded =
                            newState[rowData.id] === true;
                          newState[rowData.id] = !currentlyExpanded;
                          return newState;
                        });
                      }}
                    />
                  );
                } else if (rowData.isProject) {
                  // Employee view - project row under employee
                  const project = rowData.project as Project;
                  const employee = employees.find(
                    (emp) => emp.id === rowData.parentEmployeeId,
                  )!;

                  return (
                    <Button
                      key={`delete-${rowData.id}`}
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      style={{ fontSize: "0.75rem" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleProjectEmployeeRangeDelete(project, employee);
                      }}
                    />
                  );
                } else if (rowData.isProjectEmployee) {
                  // Project view - employee row under project
                  const projectSummary = rowData.projectSummary;
                  const employee = employees.find(
                    (emp) => emp.id === rowData.projectEmployee.id,
                  )!;

                  // Create a project object from the projectSummary for the range delete
                  const project: Project = {
                    id: rowData.parentProjectId,
                    activityId: projectSummary?.activityId || 0,
                    name: projectSummary?.name || "",
                    activity: projectSummary?.activity || "",
                    client: projectSummary?.client || "",
                    project: projectSummary?.project || "",
                    hourType: projectSummary?.hourType || "billable",
                    reference: projectSummary?.reference || "",
                    weeklyHours: rowData.projectEmployee.weeklyHours,
                    comments: rowData.projectEmployee.comments,
                    isReservation: false, // Default to false for manually created project objects
                  };

                  return (
                    <Button
                      key={`delete-${rowData.id}`}
                      icon="pi pi-trash"
                      className="p-button-text p-button-sm p-button-danger"
                      style={{ fontSize: "0.75rem" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleProjectEmployeeRangeDelete(project, employee);
                      }}
                    />
                  );
                }
                return null;
              }}
            />
            <Column
              field="name"
              header={
                <span
                  className="sort-header"
                  data-pr-tooltip="Click to sort alphabetically"
                  data-pr-position="top"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                  onClick={handleSort}
                >
                  {viewMode === "employee" ? "Employee" : "Project"}
                  {sortField === "name" && (
                    <i
                      className={`pi ${
                        sortOrder === 1 ? "pi-sort-up" : "pi-sort-down"
                      }`}
                      style={{ fontSize: "0.75rem" }}
                    />
                  )}
                  {sortField !== "name" && (
                    <i
                      className="pi pi-sort"
                      style={{ fontSize: "0.75rem", opacity: 0.5 }}
                    />
                  )}
                </span>
              }
              body={nameBodyTemplate}
              style={{ minWidth: "25rem" }}
            />
            {generateWeekColumns()}
          </DataTable>
        </div>
      </div>
      {/* </div> */}

      <SchedulingModal
        visible={schedulingModalVisible}
        onHide={closeSchedulingModal}
        initialData={modalInitialData}
        onSchedulePatch={handleSchedulePatch}
        onSave={(data) => {
          // The save operation and refresh are now handled in the modal
          closeSchedulingModal();
        }}
        onDelete={() => {
          // The delete operation and refresh are now handled in the modal
          closeSchedulingModal();
        }}
      />

      {/* Week Range Modal */}
      <WeekRangeModal
        visible={weekRangeModalVisible}
        onHide={() => setWeekRangeModalVisible(false)}
        onApply={handleWeekRangeApply}
        currentStartWeek={currentWeek}
        currentStartYear={currentYear}
        currentNumberOfWeeks={numberOfWeeks}
      />

      {/* Range Delete Modal */}
      <RangeDeleteModal
        visible={rangeDeleteModalVisible}
        onHide={closeRangeDeleteModal}
        onDelete={handleRangeDelete}
        onSchedulePatch={handleSchedulePatch}
        initialData={rangeDeleteInitialData}
      />

      {/* SpeedDial */}
      <SpeedDial
        model={[
          {
            label: "Fast Forward",
            icon: "pi pi-fast-forward",
            command: navigateFastForward,
          },
          {
            label: "Forward",
            icon: "pi pi-step-forward",
            command: navigateForward,
          },
          {
            label: "Reset view to current week",
            icon: "pi pi-undo",
            command: () => {
              setCurrentWeek(getCurrentWeek());
              setCurrentYear(new Date().getFullYear());
            },
          },
          {
            label: "Backward",
            icon: "pi pi-step-backward",
            command: navigateBackward,
          },
          {
            label: "Fast Backward",
            icon: "pi pi-fast-backward",
            command: navigateFastBackward,
          },
          {
            label: "Week Range",
            icon: "pi pi-calendar",
            command: () => setWeekRangeModalVisible(true),
          },
          {
            label: "Add Schedule",
            icon: "pi pi-plus",
            command: () => openSchedulingModal(),
          },
          {
            label: "Range Delete",
            icon: "pi pi-trash",
            command: () => openRangeDeleteModal(),
          },
        ]}
        direction="left"
        type="linear"
        showIcon="pi pi-bars"
        hideIcon="pi pi-times"
        buttonClassName="p-button-help"
        className="speeddial-bottom-right"
        style={{ position: "fixed", bottom: "20px", right: "20px" }}
        pt={{
          menu: {
            style: {
              marginRight: "10px",
            },
          },
        }}
      />
    </div>
  );
};
